// shared/client-people.js — more than one person at a client (feature sketch
// 8, docs/designs/feature-sketches.html).
//
// A CLIENT has PEOPLE: the accounts inbox invoices go to, a site manager, a
// tenant. Each has a name, an email address, a phone number, a role (free
// text, for the company's own reference) and whether they GET DOCUMENTS:
// every invoice, quote and credit note, every payment reminder and quote
// follow-up, a scheduled send, a recurring run and a resend go to everyone
// who does, in one email.
//
// They live on the client's own row (`contacts.people`, migration 132), saved
// as one field of the client through the field-level save
// (server/field-merge.js), so a phone syncs them with the client and nothing
// else. The client's own email column stays, and means the PRIMARY RECIPIENT:
// the first person who gets documents. The server keeps the two in step, so an
// app build from before people, and every reader that knows only the email,
// carry on as they did. Migration 132 made each client's email its first
// person, getting documents; a client nobody has saved since has none on its
// row, and is served one from its email (`peopleFromRow`).
//
// THE SEND SCREENS show who an email is going to and let somebody take a
// person off it or add another of the client's people, or any address, for
// that one email. What they chose travels with the send (`recipients`); a send
// that names none goes to everyone who gets documents.
//
// Pure: no imports outside shared/, no DOM, no node builtins, so the server,
// the web, the demo and the phone (through shared/ios-facade.js) all ask the
// one rule. Nothing here makes an id: the server, the browser and the phone
// each have their own way to, and JavaScriptCore has none.

import { possessive } from "./client-reference.js";

// TEN ADDRESSES AN EMAIL. Resend takes fifty, but an invoice sent to a crowd
// reads to a mail filter as a mailing, and the client's own accounts system
// may hold it back. A client with an accounts inbox, a quantity surveyor and a
// site manager is three; ten leaves room for a landlord's agent and tenants.
// The same number is the most people at one client who can get documents, so
// a client's own list never makes an email nobody could send.
export const MAX_RECIPIENTS = 10;

// FIFTY PEOPLE A CLIENT. A cap only so a client row, which every phone syncs
// whole, can't grow without end. A main contractor with several sites and a
// site manager on each is a dozen or two.
export const MAX_PEOPLE = 50;

export const PERSON_NAME_MAX_LENGTH = 100;
// A role is a word or two for the company's own reference ("Site manager",
// "Tenant, flat 3"): one line of a phone's list.
export const PERSON_ROLE_MAX_LENGTH = 60;
// The client's own phone field's cap (server/routes/contacts.js): free text,
// because international formats vary too much to check.
export const PERSON_PHONE_MAX_LENGTH = 50;
// The longest address SMTP carries (RFC 5321).
export const EMAIL_MAX_LENGTH = 254;

/** Every word a client's people and a send's recipients say. */
export const PEOPLE_COPY = {
  // The client's page.
  heading: "People",
  noPeople: "No one yet. Add the people you deal with, such as an accounts inbox or a site manager.",
  addPerson: "Add a person",
  newPersonTitle: "New person",
  editPerson: "Edit person",
  nameLabel: "Name",
  emailLabel: "Email",
  phoneLabel: "Phone",
  roleLabel: "Role",
  optional: "Optional",
  rolePrompt: "Such as quantity surveyor",
  roleHint: "For you. It isn’t printed or sent.",
  getsDocumentsLabel: "Gets invoices and quotes",
  getsDocumentsHint: "Invoices, quotes, credit notes, and reminders go to everyone who gets them, in one email.",
  // The marker on a row, as the sketch has it.
  getsDocumentsShort: "Gets invoices",
  added: "Person added",
  saved: "Person saved",
  removed: "Person removed",
  removeAction: "Remove person",
  removeTitle: "Remove this person?",
  removeMessage: "They won’t get invoices or quotes for this client from now on. Documents already sent aren’t changed. This can’t be undone.",
  // For someone who doesn't get documents, such as a site manager: nothing
  // stops reaching them, so what goes is who they are.
  removeMessageNoDocuments: "Their details come off this client. Documents already sent aren’t changed. This can’t be undone.",
  // Refusals, of a person.
  nameOrEmailMissing: "Enter the person’s name or email address.",
  emailMissingForDocuments: "Enter an email address for someone who gets invoices and quotes.",
  emailInvalid: "Enter a valid email address.",
  emailTooLong: `Shorten the email address to ${EMAIL_MAX_LENGTH} characters or fewer.`,
  nameTooLong: `Shorten the name to ${PERSON_NAME_MAX_LENGTH} characters or fewer.`,
  roleTooLong: `Shorten the role to ${PERSON_ROLE_MAX_LENGTH} characters or fewer.`,
  phoneTooLong: `Shorten the phone number to ${PERSON_PHONE_MAX_LENGTH} characters or fewer.`,
  tooMany: `A client can have up to ${MAX_PEOPLE} people. Remove one before adding another.`,
  tooManyGetDocuments: `Up to ${MAX_RECIPIENTS} people at a client can get invoices and quotes, because an email to more can be treated as bulk mail and not arrive. Turn it off for someone else first.`,
  notAList: "Couldn’t read the client’s people. Please try again.",
  saveFailed: "Couldn’t save this person. Please try again.",

  // A send's recipients.
  toLabel: "To",
  add: "Add",
  addTitle: "Add someone",
  addressLabel: "Email address",
  addressHint: "For this email only. It isn’t saved to the client.",
  addAddress: "Add address",
  remindersNote: "Reminders go to everyone at the client who gets invoices and quotes.",
  followupsNote: "Follow-ups go to everyone at the client who gets invoices and quotes.",
  scheduleNote: "Each invoice goes to everyone at the client who gets invoices and quotes.",
  noClient: "Select a client first.",
  noRecipients: "Add someone to send this to.",
  alreadyOnEmail: "That address is already on this email.",
  tooManyRecipients: `An email can go to up to ${MAX_RECIPIENTS} people. Sent to more, it can be treated as bulk mail and not arrive. Remove someone to add another.`,
  // Why a send can't go: nobody to send it to.
  noEmailAddress: "Add an email address for this client before sending.",
  noOneGetsDocuments: "No one at this client gets invoices and quotes. Turn it on for someone on the client’s page.",
  // A change to a client's people that would leave nobody to send to.
  noDocumentRecipient: "Choose someone at this client to get invoices and quotes, with an email address."
};

// Counted as a person counts characters, not UTF-16 units.
const length = (text) => [...text].length;

/** An email address as it is stored: trimmed and lower-cased. "" for none. */
export function normaliseEmail(raw) {
  if (raw == null) return "";
  return String(raw).trim().toLowerCase();
}

/**
 * Whether an address is one an email can go to. The server's rule for a
 * client's email since the start (something@something.something, no spaces),
 * and nothing that separates addresses in a list, because a sent email's
 * recipients are kept as one line of them (`recipientsColumnValue`).
 */
export function isEmailAddress(raw) {
  const email = normaliseEmail(raw);
  if (!email || email.length > EMAIL_MAX_LENGTH) return false;
  return /^[^\s@,;<>()"]+@[^\s@,;<>()"]+\.[^\s@,;<>()"]+$/.test(email);
}

/** A name or a role as it is stored: one line, its ends trimmed. */
function oneLine(raw) {
  if (raw == null) return "";
  return String(raw).replace(/\s+/g, " ").trim();
}

// An id as one is kept: what was given, trimmed and lower-cased, or "".
function normaliseId(raw) {
  if (raw == null) return "";
  return String(raw).trim().toLowerCase().slice(0, 64);
}

const yes = (value) => value === true || String(value ?? "").trim().toLowerCase() === "true";

/** A person as typed, normalised, without checking it. */
function readPerson(raw) {
  const input = raw && typeof raw === "object" ? raw : {};
  return {
    id: normaliseId(input.id),
    name: oneLine(input.name),
    email: normaliseEmail(input.email),
    phone: oneLine(input.phone),
    role: oneLine(input.role),
    getsDocuments: yes(input.getsDocuments)
  };
}

/**
 * One person somebody typed, as stored, or the sentence refusing them. The id
 * is kept as given ("" when there is none: the caller gives one). A person is
 * a name or an email address at least; one who gets documents needs the
 * address they go to.
 *
 * @returns {{ person: {id: string, name: string, email: string, phone: string, role: string, getsDocuments: boolean} } | { error: string }}
 */
export function checkPerson(raw = {}) {
  const person = readPerson(raw);
  if (!person.name && !person.email) return { error: PEOPLE_COPY.nameOrEmailMissing };
  if (length(person.name) > PERSON_NAME_MAX_LENGTH) return { error: PEOPLE_COPY.nameTooLong };
  if (person.email.length > EMAIL_MAX_LENGTH) return { error: PEOPLE_COPY.emailTooLong };
  if (person.email && !isEmailAddress(person.email)) return { error: PEOPLE_COPY.emailInvalid };
  if (person.getsDocuments && !person.email) return { error: PEOPLE_COPY.emailMissingForDocuments };
  if (length(person.phone) > PERSON_PHONE_MAX_LENGTH) return { error: PEOPLE_COPY.phoneTooLong };
  if (length(person.role) > PERSON_ROLE_MAX_LENGTH) return { error: PEOPLE_COPY.roleTooLong };
  return { person };
}

/**
 * A client's people somebody saved, as stored, or the sentence refusing them.
 * Each is checked as `checkPerson` checks them; one without an id is given one
 * by `makeId` (the server's); a second person under an id already seen is
 * dropped, so a list sent twice can't double. The order is kept: it is the
 * order they were added in, and the first who gets documents is the client's
 * primary recipient.
 */
export function checkPeople(list, { makeId = null } = {}) {
  if (list == null) return { people: [] };
  if (!Array.isArray(list)) return { error: PEOPLE_COPY.notAList };
  const seen = new Set();
  const people = [];
  for (const raw of list) {
    const checked = checkPerson(raw);
    if (checked.error) return { error: checked.error };
    const person = checked.person;
    if (!person.id) {
      if (typeof makeId !== "function") return { error: PEOPLE_COPY.notAList };
      person.id = normaliseId(makeId());
    }
    if (seen.has(person.id)) continue;
    seen.add(person.id);
    people.push(person);
  }
  if (people.length > MAX_PEOPLE) return { error: PEOPLE_COPY.tooMany };
  if (people.filter((person) => person.getsDocuments).length > MAX_RECIPIENTS) {
    return { error: PEOPLE_COPY.tooManyGetDocuments };
  }
  return { people };
}

// The id of the person a client is served when its row holds its email and
// no people: a row written by a server from before people, between the
// migration running and the new server starting. Stable, so a person changed
// from it is the same person when it comes back.
export const PRIMARY_PERSON_ID = "primary";

/**
 * The people a stored client row holds, as the API serves them. Whatever the
 * column holds, this answers a list of people: anything that isn't one is
 * left out rather than failing a whole client list. A row with no people and
 * an email is served that email as its one person, getting documents, which
 * is what migration 132 made of every client.
 */
export function peopleFromRow(stored, { email = "" } = {}) {
  let value = stored;
  if (typeof value === "string") {
    try { value = JSON.parse(value); } catch { value = []; }
  }
  const seen = new Set();
  const people = [];
  for (const raw of Array.isArray(value) ? value : []) {
    const person = readPerson(raw);
    if (!person.id || seen.has(person.id) || (!person.name && !person.email)) continue;
    // Read, never refused: one that couldn't be saved today still is who it is.
    if (person.getsDocuments && !person.email) person.getsDocuments = false;
    seen.add(person.id);
    people.push(person);
  }
  if (people.length === 0 && isEmailAddress(email)) {
    people.push({ id: PRIMARY_PERSON_ID, name: "", email: normaliseEmail(email), phone: "", role: "", getsDocuments: true });
  }
  return people;
}

/**
 * The client's primary recipient: the first person who gets documents, by
 * address. "" for nobody. This is what `contacts.email` holds.
 */
export function primaryEmail(people) {
  for (const person of Array.isArray(people) ? people : []) {
    const email = normaliseEmail(person?.email);
    if (yes(person?.getsDocuments) && email) return email;
  }
  return "";
}

/**
 * Everyone an email to the client goes to unless a send says otherwise: the
 * people who get documents, by address, in order, each once. A row with no
 * people goes to its email (`peopleFromRow`). Never more than an email can
 * carry, should an old row hold more.
 */
export function documentRecipients({ people = null, email = "" } = {}) {
  const list = peopleFromRow(people, { email });
  const seen = new Set();
  const recipients = [];
  for (const person of list) {
    if (!person.getsDocuments || !person.email || seen.has(person.email)) continue;
    seen.add(person.email);
    recipients.push(person.email);
  }
  return recipients.slice(0, MAX_RECIPIENTS);
}

/**
 * Why there is nobody to send to, for a client with no recipients: no address
 * at all (`noEmail`, which each send words for itself: "before sending",
 * "before resending"), or addresses nobody gets documents at.
 */
export function noRecipientsSentence({ people = null, email = "" } = {}, { noEmail = PEOPLE_COPY.noEmailAddress } = {}) {
  const anyAddress = peopleFromRow(people, { email }).some((person) => person.email);
  return anyAddress ? PEOPLE_COPY.noOneGetsDocuments : noEmail;
}

/**
 * Whether a client, as a change to its people or its email would leave it,
 * has somebody to send documents to: null when it does, else the sentence
 * asking for one. The apps ask it of every change to a client's people (and
 * the new client form, of its email) so no client is saved through them
 * that an invoice can't be sent to. The server doesn't: an older app build,
 * and a client saved before this, still save.
 */
export function documentRecipientProblem(people, email = "") {
  return documentRecipients({ people, email }).length ? null : PEOPLE_COPY.noDocumentRecipient;
}

/**
 * The client's people after a save of its EMAIL, from an app build that knows
 * nothing of people (or a new client's form, which asks for an email): the
 * primary recipient's address becomes the one saved. A client with nobody
 * getting documents gets the address as a new first person who does. Saved
 * empty, the primary recipient loses their address and stops getting
 * documents, and goes altogether when that leaves nothing of them.
 */
export function peopleWithEmail(people, email, { makeId = null } = {}) {
  const list = peopleFromRow(people).map((person) => ({ ...person }));
  const address = normaliseEmail(email);
  const at = list.findIndex((person) => person.getsDocuments && person.email);
  if (address) {
    if (at >= 0) {
      list[at].email = address;
      return list;
    }
    const id = typeof makeId === "function" ? normaliseId(makeId()) : PRIMARY_PERSON_ID;
    return [{ id, name: "", email: address, phone: "", role: "", getsDocuments: true }, ...list];
  }
  if (at < 0) return list;
  const person = { ...list[at], email: "", getsDocuments: false };
  if (!person.name) return list.filter((_, i) => i !== at);
  list[at] = person;
  return list;
}

/**
 * A client's people with one more, or the sentence refusing it. `person`
 * needs an id; one already there is replaced in place, so adding the same
 * person twice (a phone's queued save sent again) leaves one.
 */
export function withPersonAdded(people, person) {
  const list = peopleFromRow(people);
  const checked = checkPerson(person);
  if (checked.error) return { error: checked.error };
  if (!checked.person.id) return { error: PEOPLE_COPY.notAList };
  const at = list.findIndex((existing) => existing.id === checked.person.id);
  const after = at >= 0
    ? list.map((existing, i) => (i === at ? checked.person : existing))
    : [...list, checked.person];
  if (at < 0 && list.length >= MAX_PEOPLE) return { error: PEOPLE_COPY.tooMany };
  if (after.filter((p) => p.getsDocuments).length > MAX_RECIPIENTS) return { error: PEOPLE_COPY.tooManyGetDocuments };
  return { people: after };
}

/** A client's people with one changed in place, or the sentence refusing it. */
export function withPersonChanged(people, person) {
  return withPersonAdded(people, person);
}

/** A client's people without the one with `id`. Already gone is done. */
export function withPersonRemoved(people, id) {
  const gone = normaliseId(id);
  return { people: peopleFromRow(people).filter((person) => person.id !== gone) };
}

/**
 * What a list row says of a person, as the sketch has it: their name and role
 * ("Dan Morris · Site manager"), then how to reach them, and whether they get
 * documents. With no name, the address is the title.
 *
 * @returns {{ title: string, detail: string, getsDocuments: boolean }}
 */
export function personRow(person) {
  const p = readPerson(person);
  const title = [p.name || p.email, p.role].filter(Boolean).join(" · ");
  const detail = [p.name ? p.email : "", p.phone].filter(Boolean).join(" · ");
  return { title, detail, getsDocuments: p.getsDocuments && !!p.email };
}

/** What one person is called where they are named: their name, else their address. */
export function personLabel(person) {
  const p = readPerson(person);
  return p.name || p.email || p.phone;
}

/** The heading over a client's people in a picker: “Hallam & Pryce’s people”. */
export function clientPeopleHeading(clientName) {
  const name = String(clientName ?? "").trim();
  return name ? `${possessive(name)} people` : PEOPLE_COPY.heading;
}

/**
 * A list in words, the serial comma before "and" when there are three or
 * more (COPY.md): "a", "a and b", "a, b, and c".
 */
export function inAList(items) {
  const list = (Array.isArray(items) ? items : []).map((item) => String(item ?? "").trim()).filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`;
}

/**
 * The addresses one email goes to, as a send names them, or the sentence
 * refusing them: each a real address, each once, at least one, and no more
 * than an email can carry.
 *
 * @returns {{ recipients: string[] } | { error: string }}
 */
export function checkRecipients(list) {
  if (!Array.isArray(list)) return { error: PEOPLE_COPY.noRecipients };
  const seen = new Set();
  const recipients = [];
  for (const raw of list) {
    const email = normaliseEmail(raw);
    if (!email) continue;
    if (!isEmailAddress(email)) return { error: PEOPLE_COPY.emailInvalid };
    if (seen.has(email)) continue;
    seen.add(email);
    recipients.push(email);
  }
  if (recipients.length === 0) return { error: PEOPLE_COPY.noRecipients };
  if (recipients.length > MAX_RECIPIENTS) return { error: PEOPLE_COPY.tooManyRecipients };
  return { recipients };
}

/**
 * The recipients a send's body names, as the send will use them: `{ value }`
 * is null when it names none (everyone who gets documents), else the checked
 * list; or the sentence refusing them.
 */
export function recipientsFromBody(body) {
  if (!body || !Object.prototype.hasOwnProperty.call(body, "recipients") || body.recipients == null) {
    return { value: null };
  }
  const checked = checkRecipients(body.recipients);
  return checked.error ? { error: checked.error } : { value: checked.recipients };
}

/**
 * What a send screen shows under "To", and what its Add offers.
 *
 * `chosen` is the list somebody has made for this email, or null while they
 * haven't touched it: then it is everyone who gets documents. Each recipient
 * is named as the client's people name them, so an address that is one of
 * them reads as their name. Add offers the client's other people with an
 * address, in their order; anything else is typed.
 *
 * @returns {{ recipients: Array<{email: string, label: string, detail: string}>,
 *             addable: Array<{email: string, label: string, detail: string}>,
 *             isDefault: boolean, canAdd: boolean }}
 */
export function recipientChoices({ people = null, email = "", chosen = null } = {}) {
  const everyone = peopleFromRow(people, { email });
  const byEmail = new Map();
  for (const person of everyone) if (person.email && !byEmail.has(person.email)) byEmail.set(person.email, person);
  const isDefault = !Array.isArray(chosen);
  const list = isDefault
    ? documentRecipients({ people, email })
    : [...new Set(chosen.map(normaliseEmail).filter(Boolean))];
  const named = (address) => {
    const person = byEmail.get(address);
    return person?.name
      ? { email: address, label: person.name, detail: address }
      : { email: address, label: address, detail: "" };
  };
  const recipients = list.map(named);
  const going = new Set(list);
  const addable = [...byEmail.values()]
    .filter((person) => !going.has(person.email))
    .map((person) => named(person.email));
  return { recipients, addable, isDefault, canAdd: recipients.length < MAX_RECIPIENTS };
}

/**
 * A send's recipients with one more (a person picked or an address typed), or
 * the sentence refusing it. `current` is the list as the screen shows it.
 */
export function withRecipientAdded(current, address) {
  const email = normaliseEmail(address);
  if (!email) return { error: PEOPLE_COPY.emailInvalid };
  if (!isEmailAddress(email)) return { error: PEOPLE_COPY.emailInvalid };
  const list = (Array.isArray(current) ? current : []).map(normaliseEmail).filter(Boolean);
  if (list.includes(email)) return { error: PEOPLE_COPY.alreadyOnEmail };
  if (list.length >= MAX_RECIPIENTS) return { error: PEOPLE_COPY.tooManyRecipients };
  return { recipients: [...list, email] };
}

/** A send's recipients without one. */
export function withRecipientRemoved(current, address) {
  const gone = normaliseEmail(address);
  return { recipients: (Array.isArray(current) ? current : []).map(normaliseEmail).filter((email) => email && email !== gone) };
}

/**
 * The line Activity keeps for a send: "Sent to accounts@hallampryce.co.uk and
 * priya@hallampryce.co.uk". `verb` is "Sent" or "Resent".
 */
export function sentToSentence(recipients, { verb = "Sent" } = {}) {
  const list = inAList(recipients);
  return list ? `${verb} to ${list}` : verb;
}

/**
 * A sent email's recipients as a sent snapshot keeps them (`sent_to_email`,
 * one line of addresses), and back again. The column predates people and held
 * one address; it reads back as a list of one.
 */
export function recipientsColumnValue(recipients) {
  return (Array.isArray(recipients) ? recipients : []).map(normaliseEmail).filter(Boolean).join(", ") || null;
}

export function recipientsFromColumn(stored) {
  return String(stored ?? "").split(",").map(normaliseEmail).filter(Boolean);
}

/**
 * What a send adds to the idempotency key it hands the mail provider, so an
 * email to a different list is a different email: nothing for an email to
 * the primary recipient alone (every send before people, and every client
 * with one person, keeps the key it always had), else a short digest of the
 * addresses. FNV-1a, 32 bits: a name for a list, not a secret.
 */
export function recipientsKeySuffix(recipients, primary = "") {
  const list = (Array.isArray(recipients) ? recipients : []).map(normaliseEmail).filter(Boolean);
  if (list.length === 1 && list[0] === normaliseEmail(primary)) return "";
  let hash = 0x811c9dc5;
  for (const char of [...list].sort().join(",")) {
    hash ^= char.codePointAt(0);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `:to-${hash.toString(16).padStart(8, "0")}`;
}
